'use client';

import { c as _c } from "react/compiler-runtime";
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Pin, Plus, TerminalSquare } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import useIsElectron from '../../hooks/useIsElectron';
import { useJobTerminal } from '../../hooks/useJobTerminal';
import { useRunners } from '../../hooks/useRunners';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Error from '../common/Error';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
export const MySpacesSidebar = t0 => {
  const $ = _c(20);
  const {
    onNewSpace
  } = t0;
  const {
    currentUser
  } = useUser();
  const queryClient = useQueryClient();
  const {
    runners
  } = useRunners();
  const {
    launchSession
  } = useJobTerminal();
  const isElectron = useIsElectron();
  const canLaunch = runners.length > 0 || isElectron;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["mySpaces"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = Boolean(currentUser.isAuthenticated);
  let t3;
  if ($[1] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: _temp,
      enabled: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const mySpacesQuery = useQuery(t3);
  let t4;
  if ($[3] !== queryClient) {
    t4 = async (slug, pinned) => {
      const [error] = await client.spaces.pinSpace.mutate({
        slug,
        pinned
      });
      if (error) {
        toast(error.message, "", "error");
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["mySpaces"]
      });
      queryClient.invalidateQueries({
        queryKey: ["spacesList"]
      });
    };
    $[3] = queryClient;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const togglePin = t4;
  if (!currentUser.isAuthenticated) {
    return null;
  }
  let t5;
  if ($[5] !== canLaunch || $[6] !== launchSession || $[7] !== mySpacesQuery.data || $[8] !== togglePin) {
    t5 = mySpacesQuery.data && <div className="flex w-full flex-col gap-y-4 md:items-center laptop:items-start"><div className="flex w-full flex-col gap-y-1">{mySpacesQuery.data.spaces.slice(0, 5).map(space => <ListItem key={space.id} slug={space.spaceSlug} avatar={space.avatarKey} pinned={space.pinned ?? false} onTogglePin={() => togglePin(space.spaceSlug, !(space.pinned ?? false))} onLaunch={canLaunch ? () => launchSession({
          spaceSlug: space.spaceSlug
        }) : undefined} />)}<Link href="/spaces" className="w-full min-w-0 items-center gap-x-2 rounded-lg p-3 text-left transition-colors duration-150 hover:bg-background-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hidden laptop:flex"><span className="flex size-[35px] shrink-0 items-center justify-center rounded-full bg-background-secondary text-textSecondary"><ArrowRight size={18} /></span><span className="truncate font-emphasis text-textSecondary">View all</span></Link></div>{mySpacesQuery.data.spaces.length === 0 && <p className="p-2 text-center text-textSecondary/60 md:hidden laptop:block">You haven't joined any spaces yet.</p>}<Link title="View all spaces" href="/spaces" className="size-10 shrink-0 items-center justify-center rounded-full bg-background-secondary hover:bg-background-light md:flex laptop:hidden"><ArrowRight size={20} /></Link></div>;
    $[5] = canLaunch;
    $[6] = launchSession;
    $[7] = mySpacesQuery.data;
    $[8] = togglePin;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Plus size={20} />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== onNewSpace) {
    t7 = <button title="Add new space" onClick={onNewSpace} className="size-10 items-center justify-center rounded-full bg-background-secondary hover:bg-background-light md:flex laptop:hidden">{t6}</button>;
    $[11] = onNewSpace;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== mySpacesQuery.isError || $[14] !== mySpacesQuery.isLoading) {
    t8 = <Error loading={mySpacesQuery.isLoading} error={mySpacesQuery.isError} message="An error occured while loading your spaces. Please try again." />;
    $[13] = mySpacesQuery.isError;
    $[14] = mySpacesQuery.isLoading;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t5 || $[17] !== t7 || $[18] !== t8) {
    t9 = <div className="flex flex-col gap-y-2 md:items-center laptop:items-start">{t5}{t7}{t8}</div>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
export const SuggestedSpacesSidebar = () => {
  const $ = _c(21);
  const [suggestedModalOpen, setSuggestedModalOpen] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["discoverSuggested"],
      queryFn: _temp2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const suggestedQuery = useQuery(t0);
  let t1;
  if ($[1] !== suggestedQuery.data?.items) {
    t1 = suggestedQuery.data?.items.slice(0, 8).map(_temp3);
    $[1] = suggestedQuery.data?.items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="flex w-full flex-col gap-y-1">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== suggestedQuery.data?.items?.length) {
    t3 = suggestedQuery.data?.items?.length === 0 && <p className="p-2 text-center text-textSecondary/60">No suggestions found. Check back later.</p>;
    $[5] = suggestedQuery.data?.items?.length;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex w-full flex-col gap-y-4 p-1 md:items-center laptop:items-start">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== suggestedQuery.isError || $[11] !== suggestedQuery.isLoading) {
    t5 = <Error loading={suggestedQuery.isLoading} error={suggestedQuery.isError} message="An error occured while loading suggestions. Please try again." />;
    $[10] = suggestedQuery.isError;
    $[11] = suggestedQuery.isLoading;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setSuggestedModalOpen(true);
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <button title="View all suggestions" onClick={t6} className="size-10 items-center justify-center rounded-full bg-background-secondary hover:bg-background-light md:flex laptop:hidden"><ArrowRight size={20} /></button>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== suggestedModalOpen) {
    t8 = suggestedModalOpen && <SuggestedModal closeHandler={() => setSuggestedModalOpen(false)} />;
    $[15] = suggestedModalOpen;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t8) {
    t9 = <div className="flex flex-col gap-y-2 pb-4 md:items-center laptop:items-start">{t4}{t5}{t7}{t8}</div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
type ListItemProps = {
  slug: string;
  avatar: string | null;
  pinned: boolean;
  onTogglePin: () => void;
  /** Start a session on this board from the rail. Absent with no runner. */
  onLaunch?: () => void;
};
const ListItem = t0 => {
  const $ = _c(25);
  const {
    slug,
    avatar,
    pinned,
    onTogglePin,
    onLaunch
  } = t0;
  const t1 = `/s/${slug}`;
  let t2;
  if ($[0] !== avatar) {
    t2 = <span className="shrink-0"><Avatar spaceAvatarKey={avatar} size={35} /></span>;
    $[0] = avatar;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `s/${slug}`;
  let t4;
  if ($[2] !== slug || $[3] !== t3) {
    t4 = <p title={t3} className="truncate font-emphasis text-textPrimary md:hidden laptop:block">s/{slug}</p>;
    $[2] = slug;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t4) {
    t5 = <Link href={t1} className="flex min-w-0 flex-1 items-center gap-x-2 p-3">{t2}{t4}</Link>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== onLaunch || $[10] !== slug) {
    t6 = onLaunch && <button type="button" data-id="space-row-launch" title={`New session on ${slug}`} aria-label={`New session on ${slug}`} onClick={e => {
      e.stopPropagation();
      onLaunch();
    }} className="rounded-full p-1.5 text-textSecondary/0 transition-all duration-150 hover:!text-primary focus-visible:text-textSecondary group-hover:text-textSecondary/50 md:hidden laptop:block"><TerminalSquare size={14} /></button>;
    $[9] = onLaunch;
    $[10] = slug;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = pinned ? "Unpin space" : "Pin space";
  let t8;
  if ($[12] !== onTogglePin) {
    t8 = e_0 => {
      e_0.stopPropagation();
      onTogglePin();
    };
    $[12] = onTogglePin;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = `mr-2 rounded-full p-1.5 transition-all duration-150 md:hidden laptop:block ${pinned ? "text-primary" : "text-textSecondary/0 hover:!text-primary group-hover:text-textSecondary/50"}`;
  const t10 = pinned ? "fill-primary" : "";
  let t11;
  if ($[14] !== t10) {
    t11 = <Pin size={14} className={t10} />;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t12 = <button title={t7} onClick={t8} className={t9}>{t11}</button>;
    $[16] = t11;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12 || $[22] !== t5 || $[23] !== t6) {
    t13 = <div className="group flex w-full items-center rounded-lg transition-colors duration-150 hover:bg-background-light md:justify-center laptop:justify-start">{t5}{t6}{t12}</div>;
    $[21] = t12;
    $[22] = t5;
    $[23] = t6;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
};

// Renders a suggested item with the correct prefix and avatar type
type SuggestedListItemProps = {
  type: 'space' | 'factiii' | 'user';
  id: number;
  slug: string;
  avatarKey: string | null;
  robohash: string | null;
};
const prefixMap = {
  space: 's/',
  factiii: 'f/',
  user: '@'
};
const hrefMap = {
  space: (slug: string) => `/s/${slug}`,
  factiii: (slug: string) => `/f/${slug}`,
  user: (slug: string) => `/user/${slug}`
};
const SuggestedListItem = t0 => {
  const $ = _c(20);
  const {
    type,
    id,
    slug,
    avatarKey,
    robohash
  } = t0;
  let t1;
  if ($[0] !== avatarKey || $[1] !== id || $[2] !== robohash || $[3] !== type) {
    t1 = type === "space" ? {
      spaceAvatarKey: avatarKey
    } : type === "factiii" ? {
      factiiiAvatarKey: avatarKey,
      factiiiId: id
    } : {
      avatarKey,
      robohash,
      userId: id,
      isBot: false
    };
    $[0] = avatarKey;
    $[1] = id;
    $[2] = robohash;
    $[3] = type;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const avatarProps = t1;
  let t2;
  if ($[5] !== slug || $[6] !== type) {
    t2 = hrefMap[type](slug);
    $[5] = slug;
    $[6] = type;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== avatarProps) {
    t3 = <span className="shrink-0"><Avatar {...avatarProps} size={35} /></span>;
    $[8] = avatarProps;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = `${prefixMap[type]}${slug}`;
  const t5 = prefixMap[type];
  let t6;
  if ($[10] !== slug || $[11] !== t4 || $[12] !== t5) {
    t6 = <p title={t4} className="truncate font-emphasis text-textPrimary md:hidden laptop:block">{t5}{slug}</p>;
    $[10] = slug;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <div className="flex w-full min-w-0 items-center gap-x-2 rounded-lg p-3 transition-colors duration-150 hover:bg-background-light md:justify-center laptop:justify-start">{t3}{t6}</div>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t7) {
    t8 = <Link href={t2} className="block w-full min-w-0">{t7}</Link>;
    $[17] = t2;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};

// Modal that shows all suggested items (spaces, factiiis, users)
const SuggestedModal = t0 => {
  const $ = _c(18);
  const {
    closeHandler
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["discoverSuggested"],
      queryFn: _temp4
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const suggestedQuery = useQuery(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ModalHeader><h2>Suggested</h2></ModalHeader>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== suggestedQuery.isLoading) {
    t3 = suggestedQuery.isLoading && <p className="py-4 text-center text-textSecondary/70">Loading...</p>;
    $[2] = suggestedQuery.isLoading;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== suggestedQuery.isError) {
    t4 = suggestedQuery.isError && <p className="py-4 text-center text-destructive">Failed to load suggestions. Please try again.</p>;
    $[4] = suggestedQuery.isError;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== suggestedQuery.data?.items) {
    t5 = suggestedQuery.data?.items.map(_temp5);
    $[6] = suggestedQuery.data?.items;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== suggestedQuery.data?.items.length) {
    t6 = suggestedQuery.data?.items.length === 0 && <p className="py-4 text-center text-textSecondary/60">No suggestions found.</p>;
    $[8] = suggestedQuery.data?.items.length;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <ModalContent><div className="h-96 overflow-y-auto">{t3}{t4}{t5}{t6}</div></ModalContent>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== closeHandler || $[16] !== t7) {
    t8 = <Modal closeHandler={closeHandler}>{t2}{t7}</Modal>;
    $[15] = closeHandler;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
export default MySpacesSidebar;
function _temp() {
  return unwrapFromResult(client.spaces.my.query({
    cursor: undefined
  }));
}
function _temp2() {
  return unwrapFromResult(client.discover.suggested.query({
    cursor: undefined
  }));
}
function _temp3(item) {
  return <SuggestedListItem key={`${item.type}-${item.id}`} type={item.type} id={item.id} slug={item.slug} avatarKey={item.avatarKey} robohash={item.robohash} />;
}
function _temp4() {
  return unwrapFromResult(client.discover.suggested.query({
    cursor: undefined
  }));
}
function _temp5(item) {
  return <SuggestedListItem key={`${item.type}-${item.id}`} type={item.type} id={item.id} slug={item.slug} avatarKey={item.avatarKey} robohash={item.robohash} />;
}